/**
 * Le lien qu'un appareil ouvre pour filmer.
 *
 * # Le code dans le fragment
 *
 * Tout ce qui suit `#` reste dans le navigateur : ni le serveur du front ni un
 * journal d'accès ne le voient, et il ne part pas dans un `Referer`. Le code
 * est à usage unique et vit dix minutes, mais tant qu'il vit, il fait entrer
 * une caméra dans la séance : il n'a rien à faire dans une URL que des
 * machines enregistrent.
 *
 * # Le genre dans la requête
 *
 * Ce pour quoi l'appareil filme — `recital` — voyage en clair dans la requête :
 * la page choisit ses mots avant d'avoir réclamé le code, et ce genre n'ouvre
 * rien.
 *
 * Ce qu'il filme n'y est pas. Le lien l'a porté, quand c'était un visage ; c'est
 * désormais la séance seule qui le dit (`subjectIn`), et l'interprète le change
 * depuis le studio, devant l'image.
 *
 * # Hors de la coque
 *
 * `/camera` n'est ni une vue du studio ni une page du live : le téléphone qui
 * l'ouvre n'a rien à regarder, il filme. Voir `app/camera/page.tsx`.
 */

export const CAMERA_PAGE_PATH = "/camera";

/** 128 bits en base64url sans remplissage, comme l'API les tire. */
const CODE_PATTERN = /^[A-Za-z0-9_-]{22}$/;

/** Un genre de séance tel que l'API les nomme : quelques lettres minuscules. */
const SCOPE_PATTERN = /^[a-z]{1,32}$/;

/** Le lien à montrer en QR code, depuis cette origine, pour ce genre de séance s'il est connu. */
export function cameraPairingUrl(origin: string, code: string, scope?: string): string {
  const params = new URLSearchParams();

  if (scope !== undefined && SCOPE_PATTERN.test(scope)) {
    params.set("scope", scope);
  }

  const query = params.toString();

  return `${origin.replace(/\/$/, "")}${CAMERA_PAGE_PATH}${query === "" ? "" : `?${query}`}#${code}`;
}

/** Le genre de séance porté par la requête du lien, ou `null`. */
export function cameraScopeFromSearch(search: string): string | null {
  const scope = new URLSearchParams(search).get("scope");

  return scope !== null && SCOPE_PATTERN.test(scope) ? scope : null;
}

/** Vrai sur la page du téléphone qui filme. */
export function isCameraPagePath(pathname: string): boolean {
  return pathname === CAMERA_PAGE_PATH || pathname.startsWith(`${CAMERA_PAGE_PATH}/`);
}

/** Le code porté par un fragment d'URL, ou `null` s'il n'y en a pas de bien formé. */
export function cameraCodeFromHash(hash: string): string | null {
  const code = hash.startsWith("#") ? hash.slice(1) : hash;

  return CODE_PATTERN.test(code) ? code : null;
}
